import React, { useEffect, useState, useRef, useCallback } from "react";

/**
 * Hook that alerts clicks outside of the passed ref
 */
export function useClickAwayListener(callback, ref) {
    useEffect(() => {
        function handleClickOutside(event) {
            if (ref.current && !ref.current.contains(event.target)) {
                callback();
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [ref]);
}

export const useBlur = callback => {
    const innerRef = useRef();
    const callbackRef = useRef();

    // set current callback in ref, before second useEffect uses it
    useEffect(() => {
        // useEffect wrapper to be safe for concurrent mode
        callbackRef.current = callback;
    });

    useEffect(() => {
        document.addEventListener("click", handleClick);
        return () => document.removeEventListener("click", handleClick);

        // read most recent callback and innerRef dom node from refs
        function handleClick(e) {
            if (innerRef.current && callbackRef.current && !innerRef.current.contains(e.target)) {
                callbackRef.current(e);
            }
        }
    }, []); // no need for callback + innerRef dep

    return innerRef; // return ref; client can omit `useRef`
};

export const useScroll = callback => {
    const scrollRef = useRef();
    const callbackRef = useRef();
    const [isScrolled, setIsScrolled] = useState(false);

    // set current callback in ref, before second useEffect uses it
    useEffect(() => {
        // useEffect wrapper to be safe for concurrent mode
        callbackRef.current = callback;
    });

    useEffect(() => {
        function onScroll(e) {
            const { scrollTop } = e.target;
            if (scrollTop > 0) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }

            if (callbackRef && callbackRef.current) {
                callbackRef.current(e);
            }
        }

        if (scrollRef && scrollRef.current) {
            scrollRef.current.addEventListener("scroll", onScroll, false);

            return function cleanup() {
                scrollRef.current.removeEventListener("scroll", onScroll, false);
            };
        }
    }, []);

    return { scrollRef, isScrolled };
};

export function useAsyncState(initialState) {
    const [state, setState] = useState(initialState);
    const resolveState = useRef();
    const isMounted = useRef(false);

    useEffect(() => {
        isMounted.current = true;

        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (resolveState.current) {
            resolveState.current(state);
        }
    }, [state]);

    const setAsyncState = useCallback(
        newState =>
            new Promise(resolve => {
                if (isMounted.current) {
                    resolveState.current = resolve;
                    setState(newState);
                }
            }),
        []
    );

    return [state, setAsyncState];
}

export function useWindowSize() {
    // Initialize state with undefined width/height so server and client renders match
    // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
    const [windowSize, setWindowSize] = useState({
        width: undefined,
        height: undefined
    });
    useEffect(() => {
        // Handler to call on window resize
        function handleResize() {
            // Set window width/height to state
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight
            });
        }
        // Add event listener
        window.addEventListener("resize", handleResize);
        // Call handler right away so state gets updated with initial window size
        handleResize();
        // Remove event listener on cleanup
        return () => window.removeEventListener("resize", handleResize);
    }, []); // Empty array ensures that effect is only run on mount
    return windowSize;
}
